import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// --- Leuprolide -----------------------------------------------------

const leuprolide: ContentBlock<"medication"> = {
  id: makeId("med-leuprolide"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "during-cycle",
    "pre-cycle",
    "egg-freezing",
    "ivf",
    "endo",
    "evidence-based",
  ],
  title: "Lupron",
  subtitle: "Leuprolide - GnRH agonist for pituitary suppression or trigger",
  body: {
    genericName: "Leuprolide acetate",
    drugClass: "GnRH agonist",
    purpose:
      "Used in fertility for two distinct purposes: (1) In long/down-regulation protocols, daily micro-dose injections suppress the pituitary to prevent premature ovulation before and during stimulation. (2) As a trigger shot alternative, a single dose induces an LH surge for final egg maturation while reducing the risk of OHSS compared to hCG triggers. Also used to treat endometriosis by inducing temporary medical menopause.",
    commonRoutes: ["subcutaneous", "intramuscular"],
    commonForms: ["vial"],
    dosageRanges:
      "Micro-dose flare: 20-40 mcg twice daily. Down-regulation: 0.5-1.0 mg daily, reduced to 0.25 mg during stimulation. Trigger: 1-4 mg single dose. Depot (for endometriosis): 3.75 mg monthly or 11.25 mg every 3 months.",
    howToTake:
      "Step 1: Prepare - Wash hands thoroughly. Gather an insulin syringe (28-31G, ½ inch), alcohol swabs, and your Lupron vial. Clean the vial top with an alcohol swab. | Step 2: Draw dose - Draw your prescribed dose carefully. Lupron uses very small volumes - read the syringe markings precisely. For micro-dose protocols, your clinic may instruct dilution with bacteriostatic water; follow those instructions exactly. | Step 3: Clean site - Clean the injection site on your lower abdomen (1-2 inches from the navel) or upper thigh with an alcohol swab and let dry completely. | Step 4: Inject - Pinch the skin, insert the needle at a 90° angle, and inject slowly. For trigger use, inject the single prescribed dose 34-36 hours before retrieval. | Step 5: After injection - Release the pinch and withdraw the needle. Do not rub the site - apply gentle pressure with gauze if needed. Rotate injection sites daily to prevent irritation. Inject at the same time each day. | ⚠ Missed dose - If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic. | ⚠ When to call - Call your clinic for severe site reactions (spreading redness, warmth, or pus), signs of allergic reaction, or any unusual symptoms.",
    howToTakeByForm: {
      vial: "Step 1: Prepare - Use an insulin syringe (28-31G) to draw your prescribed dose from the Lupron vial. These syringes have fine needles ideal for the small subcutaneous volumes required. | Step 2: Draw dose - For micro-dose protocols, your clinic may instruct dilution with bacteriostatic water before use - follow their dilution instructions exactly, as the concentration changes your dose volume. Draw to the prescribed marking. | Step 3: Clean site - Clean the injection site on your lower abdomen or upper thigh with an alcohol swab and let dry completely. | Step 4: Inject - Pinch the skin, insert the needle at 90°, and inject slowly. | Step 5: After injection - Release the pinch, withdraw the needle, and apply gentle pressure. Do not rub. Rotate sites daily. Store the multi-dose vial as directed - once opened, it may be kept at room temperature for up to 2 weeks. | ⚠ Missed dose - If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
    },
    sideEffects: [
      "Hot flashes (especially with prolonged use)",
      "Headache",
      "Injection site reactions",
      "Mood changes and irritability",
      "Vaginal dryness (with prolonged use)",
      "Joint aches",
      "Insomnia",
      "Temporary worsening of symptoms (flare effect) in first 1-2 days",
    ],
    storage:
      "Store in the refrigerator (2-8°C / 36-46°F). The daily-use vial may be kept at room temperature (up to 25°C / 77°F) for up to 2 weeks once opened. Protect from light.",
    brandsByCountry: [
      { country: "US", brands: ["Lupron", "Lupron Depot"] },
      { country: "UK", brands: ["Prostap", "Lucrin"] },
      { country: "EU", brands: ["Lucrin", "Enantone"] },
      { country: "AU", brands: ["Lucrin", "Eligard"] },
      {
        country: "FR",
        brands: ["Enantone"],
        cisByBrand: {
          Enantone: "65738047",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (DailyMed, emc, TGA, Medsafe, MIMS
    // Asia, Takeda/AbbVie). Brand varies widely by market. Oncology-only
    // depots (Eligard, Camcevi, Lutrate, etc.) are excluded as they are not
    // used in fertility.
    availability: [
      { name: "Lupron", manufacturer: "AbbVie", countries: ["US", "CA", "BR"] },
      { name: "Prostap", manufacturer: "Takeda", countries: ["GB", "IE"] },
      {
        name: "Lucrin",
        manufacturer: "AbbVie",
        countries: ["AU", "NZ", "CH", "SG", "MY", "VN", "NL", "MX"],
      },
      { name: "Procrin", manufacturer: "AbbVie", countries: ["ES", "MX"] },
      {
        name: "Procren",
        manufacturer: "AbbVie",
        countries: ["FI", "NO", "SE"],
      },
      {
        name: "Enantone",
        manufacturer: "Takeda",
        countries: ["DE", "FR", "IT", "AT", "HK", "TH"],
      },
      { name: "Leuplin", manufacturer: "Takeda", countries: ["JP"] },
      { name: "Luprolex", manufacturer: "Takeda", countries: ["PH"] },
      { name: "Tapros", manufacturer: "Takeda", countries: ["ID"] },
    ],
    aliases: [
      "Leuprolide",
      "Leuproréline",
      "Énantone",
      "Eligard",
      "Leuprorelin",
    ],
    resources: [
      {
        label: "How to inject Lupron / leuprolide (ReUnite Rx)",
        url: "https://www.youtube.com/watch?v=7qvKBKb1iNk",
        type: "video",
      },
    ],
    forWhom: "her",
    frBody: {
      genericName: "Acétate de leuproréline",
      drugClass: "Agoniste de la GnRH",
      purpose:
        "Utilisée en fertilité pour deux indications distinctes : (1) Dans les protocoles longs (freinage hypophysaire), des micro-injections quotidiennes bloquent l'hypophyse pour empêcher une ovulation prématurée avant et pendant la stimulation. (2) Comme alternative au déclencheur classique, une dose unique provoque un pic de LH pour la maturation finale des ovocytes tout en réduisant le risque de SHO par rapport aux déclencheurs à base d'hCG. Également utilisée dans le traitement de l'endométriose en induisant une ménopause médicale temporaire.",
      dosageRanges:
        "Protocole micro-dose flare : 20-40 µg deux fois par jour. Freinage hypophysaire : 0,5-1,0 mg par jour, réduit à 0,25 mg pendant la stimulation. Déclencheur : 1-4 mg en dose unique. Forme retard (endométriose) : 3,75 mg par mois ou 11,25 mg tous les 3 mois.",
      howToTake:
        "Préparer - Lavez-vous soigneusement les mains. Préparez une seringue à insuline (28-31 G, 12 mm), des compresses alcoolisées et votre flacon de Lupron. Désinfectez le bouchon du flacon à l'alcool. | Prélever la dose - Aspirez la dose prescrite avec précision. Le Lupron s'utilise à très faibles volumes - lisez précisément les graduations. Pour les protocoles micro-dose, votre centre peut demander une dilution avec de l'eau bactériostatique ; suivez ses instructions à la lettre. | Désinfecter le site - Désinfectez le bas de l'abdomen (à 2-5 cm du nombril) ou le haut de la cuisse à l'alcool et laissez sécher complètement. | Injecter - Pincez la peau, insérez l'aiguille à 90° et injectez lentement. Pour un usage comme déclencheur, injectez la dose unique prescrite 34 à 36 heures avant la ponction. | Après l'injection - Relâchez le pli cutané et retirez l'aiguille. Ne frottez pas le site - appliquez si besoin une légère pression avec une compresse. Alternez les sites d'injection chaque jour pour limiter l'irritation. Injectez à la même heure chaque jour. | ⚠ Dose oubliée - Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre. | ⚠ Quand contacter - Contactez votre centre en cas de réaction sévère au point d'injection (rougeur qui s'étend, chaleur, écoulement purulent), de signes de réaction allergique ou de tout symptôme inhabituel.",
      howToTakeByForm: {
        vial: "Préparer - Utilisez une seringue à insuline (28-31 G) pour prélever la dose prescrite dans le flacon de Lupron. Ces seringues ont une aiguille fine adaptée aux petits volumes sous-cutanés requis. | Prélever la dose - Pour les protocoles micro-dose, votre centre peut demander une dilution préalable avec de l'eau bactériostatique - suivez ses instructions à la lettre, la concentration change le volume de votre dose. Prélevez jusqu'à la graduation prescrite. | Désinfecter le site - Désinfectez le site d'injection sur le bas de l'abdomen ou le haut de la cuisse à l'alcool et laissez sécher complètement. | Injecter - Pincez la peau, insérez l'aiguille à 90° et injectez lentement. | Après l'injection - Relâchez le pli, retirez l'aiguille et appliquez une légère pression. Ne pas frotter. Alternez les sites chaque jour. Conservez le flacon multidose selon les indications - après ouverture, il peut rester à température ambiante jusqu'à 2 semaines. | ⚠ Dose oubliée - Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre.",
      },
      sideEffects: [
        "Bouffées de chaleur (surtout en cas d'utilisation prolongée)",
        "Maux de tête",
        "Réactions au point d'injection",
        "Changements d'humeur et irritabilité",
        "Sécheresse vaginale (en cas d'utilisation prolongée)",
        "Douleurs articulaires",
        "Insomnie",
        "Aggravation transitoire des symptômes (effet flare) durant les 1-2 premiers jours",
      ],
      storage:
        "Conservez au réfrigérateur (2-8 °C). Le flacon à usage quotidien peut être conservé à température ambiante (jusqu'à 25 °C) pendant 2 semaines maximum après ouverture. À protéger de la lumière.",
    },
  },
};

// --- Triptorelin ----------------------------------------------------

const triptorelin: ContentBlock<"medication"> = {
  id: makeId("med-triptorelin"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "during-cycle",
    "pre-cycle",
    "ivf",
    "evidence-based",
  ],
  title: "Decapeptyl",
  subtitle: "Triptorelin - GnRH agonist for pituitary suppression or trigger",
  body: {
    genericName: "Triptorelin",
    drugClass: "GnRH agonist",
    purpose:
      "Used for pituitary down-regulation in long IVF protocols and as an alternative trigger for final oocyte maturation. In long protocols, daily subcutaneous injections suppress ovarian function before stimulation begins. As a trigger, it induces an endogenous LH surge with lower OHSS risk than hCG.",
    commonRoutes: ["subcutaneous", "intramuscular"],
    commonForms: ["vial"],
    dosageRanges:
      "Daily suppression: 0.1 mg subcutaneous. Depot: 3.75 mg intramuscular single dose. Trigger: 0.1-0.2 mg single dose.",
    howToTake:
      "Step 1: Prepare - Wash hands and gather supplies: syringe, reconstitution needle, subcutaneous needle (27-30G, ½ inch), alcohol swabs, and medication vial. | Step 2: Reconstitute dose - Inject the provided sterile water into the powder vial and swirl gently - never shake. Inspect to confirm the solution is clear and particle-free. Draw the prescribed dose and switch to the injection needle. | Step 3: Clean site - Clean the injection site on your lower abdomen with an alcohol swab and let dry completely. | Step 4: Inject - Pinch the skin, insert the needle at 90°, and inject slowly. Start on approximately cycle day 21 and continue as directed. For trigger use, inject the prescribed single dose 34-36 hours before retrieval. | Step 5: After injection - Release the pinch, withdraw the needle, and apply gentle pressure without rubbing. Rotate injection sites daily. Use reconstituted solution immediately - do not store. | ⚠ Missed dose - If you miss a daily dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic. | ⚠ When to call - Call your clinic for severe injection site reactions, spreading redness or warmth, signs of allergic reaction, or unusual symptoms.",
    howToTakeByForm: {
      vial: "Step 1: Prepare - Wash hands and gather the vial, reconstitution needle, subcutaneous needle (27-30G, ½ inch), and alcohol swabs. | Step 2: Reconstitute dose - Clean the vial top with alcohol. Inject the provided sterile water into the powder vial using the reconstitution needle and swirl gently until fully dissolved - never shake. Confirm the solution is clear and free of particles. Draw the prescribed dose into the syringe, then switch to the subcutaneous needle. | Step 3: Clean site - Clean the injection site on your lower abdomen with an alcohol swab and let dry. | Step 4: Inject - Pinch the skin, insert the needle at 90°, and inject slowly. | Step 5: After injection - Release the pinch, withdraw the needle, and apply gentle pressure. Do not rub. Rotate injection sites daily. Use the reconstituted solution immediately - do not store. | ⚠ Missed dose - If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
    },
    sideEffects: [
      "Hot flashes",
      "Headache",
      "Injection site reactions",
      "Mood changes",
      "Breakthrough bleeding",
      "Temporary flare effect (first few days)",
      "Fatigue",
    ],
    storage:
      "Store in the refrigerator (2-8°C / 36-46°F). Depot formulation should be reconstituted immediately before use. Protect from light.",
    brandsByCountry: [
      { country: "US", brands: ["Trelstar"] },
      { country: "UK", brands: ["Decapeptyl", "Gonapeptyl"] },
      { country: "EU", brands: ["Decapeptyl", "Gonapeptyl", "Diphereline"] },
      { country: "AU", brands: ["Decapeptyl", "Diphereline"] },
      {
        country: "FR",
        brands: ["Décapeptyl"],
        cisByBrand: {
          Décapeptyl: "62842966",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (emc, France BDPM, TGA, MIMS Asia,
    // Ipsen/Ferring). Oncology-only brands (Trelstar, Pamorelin) and the
    // non-fertility Salvacyl are excluded.
    availability: [
      {
        name: "Decapeptyl",
        manufacturer: "Ipsen",
        countries: [
          "FR",
          "DE",
          "ES",
          "IT",
          "BE",
          "IE",
          "PT",
          "AU",
          "SG",
          "HK",
          "TH",
          "ID",
          "IN",
          "KR",
        ],
      },
      {
        name: "Gonapeptyl",
        manufacturer: "Ferring",
        countries: ["GB", "DE", "EU", "VN", "IN"],
      },
      {
        name: "Diphereline",
        manufacturer: "Ipsen",
        countries: [
          "SG",
          "MY",
          "PH",
          "ID",
          "IN",
          "PL",
          "BG",
          "CZ",
          "LT",
          "RO",
          "TH",
          "VN",
          "HK",
          "AU",
          "KR",
        ],
      },
      { name: "Fertipeptil", manufacturer: "Ferring", countries: ["VN"] },
    ],
    aliases: ["Triptorelin", "Triptoréline", "Décapeptyl"],
    forWhom: "her",
    frBody: {
      genericName: "Triptoréline",
      drugClass: "Agoniste de la GnRH",
      purpose:
        "Utilisée pour le freinage hypophysaire dans les protocoles longs de FIV et comme alternative au déclencheur pour la maturation finale des ovocytes. Dans les protocoles longs, des injections sous-cutanées quotidiennes suppriment la fonction ovarienne avant le début de la stimulation. En tant que déclencheur, elle induit un pic endogène de LH avec un risque de SHO plus faible qu'avec l'hCG.",
      dosageRanges:
        "Suppression quotidienne : 0,1 mg en sous-cutané. Forme retard : 3,75 mg en intramusculaire, dose unique. Déclencheur : 0,1-0,2 mg en dose unique.",
      howToTake:
        "Préparer - Lavez-vous les mains et préparez le matériel : seringue, aiguille de reconstitution, aiguille d'injection sous-cutanée (27-30 G, 12 mm), compresses alcoolisées et flacon. | Reconstituer la dose - Injectez l'eau stérile fournie dans le flacon de poudre et faites tourner doucement - sans agiter. Vérifiez que la solution est claire et sans particules. Prélevez la dose prescrite et changez pour l'aiguille d'injection. | Désinfecter le site - Désinfectez le site d'injection sur le bas de l'abdomen à l'alcool et laissez sécher complètement. | Injecter - Pincez la peau, insérez l'aiguille à 90° et injectez lentement. Démarrez vers le 21e jour du cycle et poursuivez selon les indications. Pour un usage comme déclencheur, injectez la dose unique prescrite 34 à 36 heures avant la ponction. | Après l'injection - Relâchez le pli, retirez l'aiguille et appliquez une légère pression sans frotter. Alternez les sites chaque jour. Utilisez la solution reconstituée immédiatement - ne pas conserver. | ⚠ Dose oubliée - Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre. | ⚠ Quand contacter - Contactez votre centre en cas de réaction sévère au point d'injection, de rougeur ou chaleur qui s'étend, de signes de réaction allergique ou de symptômes inhabituels.",
      howToTakeByForm: {
        vial: "Préparer - Lavez-vous les mains et préparez le flacon, l'aiguille de reconstitution, l'aiguille sous-cutanée (27-30 G, 12 mm) et des compresses alcoolisées. | Reconstituer la dose - Désinfectez le bouchon du flacon à l'alcool. Injectez l'eau stérile fournie dans le flacon de poudre avec l'aiguille de reconstitution et faites tourner doucement jusqu'à dissolution complète - sans agiter. Vérifiez que la solution est claire et sans particules. Prélevez la dose prescrite dans la seringue, puis changez pour l'aiguille sous-cutanée. | Désinfecter le site - Désinfectez le site sur le bas de l'abdomen à l'alcool et laissez sécher. | Injecter - Pincez la peau, insérez l'aiguille à 90° et injectez lentement. | Après l'injection - Relâchez le pli, retirez l'aiguille et appliquez une légère pression. Ne pas frotter. Alternez les sites chaque jour. Utilisez la solution reconstituée immédiatement - ne pas conserver. | ⚠ Dose oubliée - Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre.",
      },
      sideEffects: [
        "Bouffées de chaleur",
        "Maux de tête",
        "Réactions au point d'injection",
        "Changements d'humeur",
        "Saignements intermenstruels",
        "Effet flare transitoire (premiers jours)",
        "Fatigue",
      ],
      storage:
        "Conservez au réfrigérateur (2-8 °C). La forme retard doit être reconstituée juste avant utilisation. À protéger de la lumière.",
    },
  },
};

// --- Buserelin ------------------------------------------------------

const buserelin: ContentBlock<"medication"> = {
  id: makeId("med-buserelin"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "during-cycle",
    "pre-cycle",
    "ivf",
    "evidence-based",
  ],
  title: "Suprefact",
  subtitle: "Buserelin - GnRH agonist nasal spray or injection for suppression",
  body: {
    genericName: "Buserelin",
    drugClass: "GnRH agonist",
    purpose:
      "Suppresses the pituitary gland to prevent premature ovulation in long-protocol IVF cycles. Buserelin is notable for being available as a nasal spray, offering a needle-free option for pituitary suppression. Also used as a trigger shot in some protocols.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["vial"],
    dosageRanges:
      "Injection: 0.2-0.5 mg daily subcutaneous. Nasal spray: 150 mcg (one spray) into each nostril 4 times daily (total 1200 mcg/day), reduced during stimulation.",
    howToTake:
      "Step 1: Prepare - For injection: wash hands and gather your syringe, subcutaneous needle (27-30G, ½ inch), alcohol swabs, and Buserelin vial. Clean the vial top with alcohol. For nasal spray: have the bottle ready and blow your nose gently beforehand if needed. | Step 2: Draw dose / Administer spray - For injection: draw the prescribed dose. Check for air bubbles - tap the syringe and push air out before injecting. For nasal spray: spray once into each nostril four times daily at regular intervals, starting approximately on cycle day 21. | Step 3: Clean site (injection only) - Clean the injection site on your lower abdomen (1-2 inches from the navel) or upper thigh with an alcohol swab and let dry completely. | Step 4: Inject - Pinch the skin firmly, insert the needle at a 90° angle, and inject slowly and steadily. Dose may be reduced once ovarian stimulation begins - follow your clinic's adjusted instructions. | Step 5: After injection / After spray - For injection: release the pinch, withdraw the needle, and apply gentle pressure without rubbing. Rotate injection sites daily. Administer at the same time each day for consistent suppression. For nasal spray: avoid blowing your nose for 30 minutes after use. | ⚠ Missed dose - If you miss an injection dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic. | ⚠ When to call - Call your clinic for severe site reactions (spreading redness, warmth, or pus), allergic reaction signs, or unusual symptoms.",
    howToTakeByForm: {
      vial: "Step 1: Prepare - Wash hands and gather your subcutaneous needle (27-30G, ½ inch), alcohol swabs, and Buserelin vial. Clean the vial top with an alcohol swab. | Step 2: Draw dose - Draw the prescribed dose carefully. Check for air bubbles - tap the syringe and push air out before injecting. | Step 3: Clean site - Clean the injection site on your lower abdomen or upper thigh with an alcohol swab and let dry completely. | Step 4: Inject - Pinch the skin firmly, insert the needle at 90°, and inject slowly and steadily. Administer at the same time each day. Dose may be reduced once ovarian stimulation begins - follow your clinic's adjusted instructions. | Step 5: After injection - Release the pinch, withdraw the needle, and apply gentle pressure with gauze - do not rub. Rotate between left and right sides daily. | ⚠ Missed dose - If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
    },
    sideEffects: [
      "Hot flashes",
      "Headache",
      "Nasal irritation (spray form)",
      "Mood swings",
      "Vaginal dryness",
      "Reduced libido",
      "Injection site reactions (injection form)",
    ],
    storage:
      "Store in the refrigerator (2-8°C / 36-46°F). Nasal spray can be stored at room temperature once opened - use within the timeframe specified on the packaging. Protect from light.",
    brandsByCountry: [
      { country: "US", brands: ["Suprefact"] },
      { country: "UK", brands: ["Suprefact"] },
      { country: "EU", brands: ["Suprefact"] },
      { country: "AU", brands: ["Suprefact"] },
      { country: "FR", brands: ["Suprefact"] },
    ],
    // Availability deep-researched Jun 2026 (Health Canada, emc, MIMS
    // SG/MY/HK, EMA). Not marketed in the US or Australia.
    availability: [
      {
        name: "Suprefact",
        manufacturer: "Sanofi",
        countries: ["CA", "GB", "SG", "MY"],
      },
      {
        name: "Suprecur",
        manufacturer: "Sanofi",
        countries: ["GB", "DE", "HK"],
      },
      { name: "Profact", manufacturer: "Sanofi", countries: ["DE"] },
    ],
    aliases: ["Buserelin", "Buséréline"],
    forWhom: "her",
    frBody: {
      genericName: "Buséréline",
      drugClass: "Agoniste de la GnRH",
      purpose:
        "Bloque l'hypophyse pour empêcher une ovulation prématurée dans les cycles de FIV en protocole long. La buséréline est notable car elle existe en spray nasal, offrant une option sans aiguille pour la suppression hypophysaire. Également utilisée comme déclencheur dans certains protocoles.",
      dosageRanges:
        "Injection : 0,2-0,5 mg par jour en sous-cutané. Spray nasal : 150 µg (une pulvérisation) dans chaque narine 4 fois par jour (1200 µg/jour au total), dose réduite pendant la stimulation.",
      howToTake:
        "Préparer - Pour l'injection : lavez-vous les mains et préparez votre seringue, l'aiguille sous-cutanée (27-30 G, 12 mm), des compresses alcoolisées et votre flacon de Buséréline. Désinfectez le bouchon du flacon à l'alcool. Pour le spray nasal : préparez le flacon et mouchez-vous doucement au préalable si nécessaire. | Prélever la dose / Administrer le spray - Pour l'injection : prélevez la dose prescrite. Vérifiez l'absence de bulles - tapotez la seringue et chassez l'air avant l'injection. Pour le spray nasal : pulvérisez une fois dans chaque narine quatre fois par jour à intervalles réguliers, en commençant vers le 21e jour du cycle. | Désinfecter le site (injection uniquement) - Désinfectez le site sur le bas de l'abdomen (à 2-5 cm du nombril) ou le haut de la cuisse à l'alcool et laissez sécher complètement. | Injecter - Pincez fermement la peau, insérez l'aiguille à 90° et injectez lentement et régulièrement. La dose peut être réduite au début de la stimulation ovarienne - suivez les instructions adaptées de votre centre. | Après l'injection / Après le spray - Pour l'injection : relâchez le pli, retirez l'aiguille et appliquez une légère pression sans frotter. Alternez les sites chaque jour. Administrez à la même heure chaque jour pour une suppression régulière. Pour le spray nasal : évitez de vous moucher pendant 30 minutes après l'utilisation. | ⚠ Dose oubliée - Pour une injection oubliée de moins de 2 heures, injectez immédiatement. Au-delà, contactez votre centre. | ⚠ Quand contacter - Contactez votre centre en cas de réaction sévère au point d'injection (rougeur qui s'étend, chaleur, écoulement purulent), de signes de réaction allergique ou de symptômes inhabituels.",
      howToTakeByForm: {
        vial: "Préparer - Lavez-vous les mains et préparez votre aiguille sous-cutanée (27-30 G, 12 mm), des compresses alcoolisées et votre flacon. Désinfectez le bouchon du flacon. | Prélever la dose - Prélevez soigneusement la dose prescrite. Vérifiez l'absence de bulles - tapotez la seringue et chassez l'air avant l'injection. | Désinfecter le site - Désinfectez le site sur le bas de l'abdomen ou le haut de la cuisse à l'alcool et laissez sécher complètement. | Injecter - Pincez fermement la peau, insérez l'aiguille à 90° et injectez lentement et régulièrement. Administrez à la même heure chaque jour. La dose peut être réduite au début de la stimulation - suivez les instructions adaptées de votre centre. | Après l'injection - Relâchez le pli, retirez l'aiguille et appliquez une légère pression avec une compresse - sans frotter. Alternez côté gauche et droit chaque jour. | ⚠ Dose oubliée - Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre.",
      },
      sideEffects: [
        "Bouffées de chaleur",
        "Maux de tête",
        "Irritation nasale (forme spray)",
        "Sautes d'humeur",
        "Sécheresse vaginale",
        "Baisse de libido",
        "Réactions au point d'injection (forme injectable)",
      ],
      storage:
        "Conservez au réfrigérateur (2-8 °C). Le spray nasal peut être conservé à température ambiante après ouverture - à utiliser dans le délai indiqué sur l'emballage. À protéger de la lumière.",
    },
  },
};

// --- Goserelin ------------------------------------------------------

const goserelin: ContentBlock<"medication"> = {
  id: makeId("med-goserelin"),
  kind: "medication",
  tags: ["medications", "her", "pre-cycle", "endo", "ivf", "evidence-based"],
  title: "Zoladex",
  subtitle: "Goserelin - GnRH agonist depot implant for suppression",
  body: {
    genericName: "Goserelin",
    drugClass: "GnRH agonist",
    purpose:
      "A depot GnRH agonist administered as a small implant under the skin. In fertility, it is primarily used to suppress ovarian function before IVF in endometriosis patients, or for pituitary down-regulation in long protocols. The depot formulation provides sustained release over 1-3 months, eliminating the need for daily injections.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["vial"],
    dosageRanges:
      "3.6 mg every 28 days or 10.8 mg every 12 weeks (depot implant)",
    howToTake:
      "Step 1: What to expect - Zoladex is administered by your clinic, not self-injected. A small pellet implant is inserted under the skin of your lower abdomen using a specialized applicator. | Step 2: The procedure - Your clinician will clean the injection site and may apply local anesthesia. The implant is placed in the anterior abdominal wall with a single insertion. The procedure takes only a few minutes. | Step 3: After the implant - The implant slowly releases goserelin over 4 or 12 weeks depending on the formulation. You may notice mild bruising or tenderness at the site for a day or two - this is normal. | ⚠ What to watch for - Contact your clinic if you experience significant pain, redness spreading from the site, signs of infection, or if you think the implant may have moved.",
    sideEffects: [
      "Hot flashes",
      "Headache",
      "Injection site pain or bruising",
      "Mood changes and depression",
      "Vaginal dryness",
      "Decreased bone density (with prolonged use)",
      "Joint pain",
      "Breast tenderness",
    ],
    storage:
      "Store at room temperature (up to 25°C / 77°F). Do not remove from the foil pouch until immediately before use. Administered in clinic.",
    brandsByCountry: [
      { country: "US", brands: ["Zoladex"] },
      { country: "UK", brands: ["Zoladex"] },
      { country: "EU", brands: ["Zoladex"] },
      { country: "AU", brands: ["Zoladex"] },
      {
        country: "FR",
        brands: ["Zoladex"],
        cisByBrand: {
          Zoladex: "67037162",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (emc, TGA, KEGG, MIMS Asia,
    // AstraZeneca). Single global brand Zoladex.
    availability: [
      {
        name: "Zoladex",
        manufacturer: "AstraZeneca",
        countries: [
          "GB",
          "US",
          "CA",
          "AU",
          "JP",
          "EU",
          "SG",
          "MY",
          "PH",
          "HK",
          "TH",
          "ID",
          "IN",
          "BR",
          "MX",
        ],
      },
      { name: "Goselin", countries: ["IN"] },
    ],
    aliases: ["Goserelin", "Goséréline"],
    forWhom: "her",
    frBody: {
      genericName: "Goséréline",
      drugClass: "Agoniste de la GnRH",
      purpose:
        "Agoniste de la GnRH à libération prolongée administré sous forme de petit implant sous-cutané. En fertilité, il est principalement utilisé pour bloquer la fonction ovarienne avant une FIV chez les patientes atteintes d'endométriose, ou pour le freinage hypophysaire dans les protocoles longs. La forme retard assure une libération continue sur 1 à 3 mois, évitant les injections quotidiennes.",
      dosageRanges:
        "3,6 mg tous les 28 jours ou 10,8 mg toutes les 12 semaines (implant retard).",
      howToTake:
        "À quoi s'attendre - Le Zoladex est administré par votre centre, pas en auto-injection. Un petit implant en forme de granulé est inséré sous la peau du bas de l'abdomen avec un applicateur dédié. | Le geste - Votre praticien désinfecte la zone et peut appliquer une anesthésie locale. L'implant est placé dans la paroi abdominale antérieure en une seule insertion. Le geste prend quelques minutes. | Après la pose - L'implant libère lentement la goséréline sur 4 ou 12 semaines selon la forme. Vous pouvez observer un léger hématome ou une sensibilité au point d'insertion pendant un à deux jours - c'est normal. | ⚠ À surveiller - Contactez votre centre en cas de douleur importante, de rougeur qui s'étend, de signes d'infection ou si vous pensez que l'implant a bougé.",
      sideEffects: [
        "Bouffées de chaleur",
        "Maux de tête",
        "Douleur ou ecchymose au point d'injection",
        "Changements d'humeur et dépression",
        "Sécheresse vaginale",
        "Diminution de la densité osseuse (en cas d'utilisation prolongée)",
        "Douleurs articulaires",
        "Sensibilité des seins",
      ],
      storage:
        "Conservez à température ambiante (jusqu'à 25 °C). Ne pas retirer du blister avant l'utilisation immédiate. Administré en clinique.",
    },
  },
};

// --- Nafarelin ------------------------------------------------------

const nafarelin: ContentBlock<"medication"> = {
  id: makeId("med-nafarelin"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "pre-cycle",
    "during-cycle",
    "ivf",
    "endo",
    "evidence-based",
  ],
  title: "Synarel",
  subtitle: "Nafarelin - GnRH agonist nasal spray for pituitary suppression",
  body: {
    genericName: "Nafarelin",
    drugClass: "GnRH agonist",
    purpose:
      "A GnRH agonist delivered exclusively as a nasal spray, used for pituitary down-regulation in IVF long protocols and for treatment of endometriosis. Provides a completely needle-free alternative for patients who need GnRH agonist suppression.",
    commonRoutes: ["topical"],
    commonForms: ["vial"],
    dosageRanges:
      "IVF suppression: 200-400 mcg twice daily (one or two sprays per nostril, twice daily). Endometriosis: 200 mcg twice daily for up to 6 months.",
    howToTake:
      "Step 1: Prepare - Prime the bottle before first use by pumping into the air until a fine mist appears. Blow your nose gently if congested. | Step 2: Administer spray - Tilt your head slightly forward, insert the spray tip into one nostril, and press firmly once. Spray into one nostril in the morning and the other nostril in the evening, or as directed by your clinic. Start on cycle day 21 for down-regulation protocols. | Step 3: After use - Avoid sneezing or blowing your nose for at least 30 minutes after each dose to ensure full absorption. | ⚠ Missed dose - If you miss a dose, take it as soon as you remember unless it is almost time for your next scheduled dose - then skip the missed dose and continue. Do not double up. | ⚠ When to call - If you have a cold or nasal congestion, contact your clinic as absorption may be affected. Call if you experience unusual symptoms or breakthrough bleeding outside the expected pattern.",
    sideEffects: [
      "Nasal irritation and dryness",
      "Hot flashes",
      "Headache",
      "Mood swings",
      "Vaginal dryness",
      "Decreased libido",
      "Acne",
      "Muscle pain",
    ],
    storage:
      "Store upright at room temperature (15-30°C / 59-86°F). Protect from light. Keep the bottle upright during storage.",
    brandsByCountry: [
      { country: "US", brands: ["Synarel"] },
      { country: "UK", brands: ["Synarel"] },
      { country: "EU", brands: ["Synarel"] },
      { country: "AU", brands: ["Synarel"] },
      {
        country: "FR",
        brands: ["Synarel"],
        cisByBrand: {
          Synarel: "63954950",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (DailyMed, emc, TGA, Pfizer).
    // Barely marketed in Asia; Synarela is the German spelling.
    availability: [
      {
        name: "Synarel",
        manufacturer: "Pfizer",
        countries: ["US", "GB", "CA", "AU", "IE", "DE"],
      },
    ],
    aliases: ["Nafarelin", "Nafaréline"],
    forWhom: "her",
    frBody: {
      genericName: "Nafaréline",
      drugClass: "Agoniste de la GnRH",
      purpose:
        "Agoniste de la GnRH disponible exclusivement en spray nasal, utilisé pour le freinage hypophysaire dans les protocoles longs de FIV et pour le traitement de l'endométriose. Offre une alternative entièrement sans aiguille aux patientes qui nécessitent un freinage par agoniste de la GnRH.",
      dosageRanges:
        "Suppression en FIV : 200-400 µg deux fois par jour (1 à 2 pulvérisations par narine, deux fois par jour). Endométriose : 200 µg deux fois par jour pendant 6 mois maximum.",
      howToTake:
        "Préparer - Amorcez le flacon avant la première utilisation en pompant à l'air jusqu'à voir une fine brume. Mouchez-vous doucement si vous êtes congestionnée. | Administrer le spray - Inclinez légèrement la tête vers l'avant, insérez l'embout dans une narine et pressez fermement une fois. Pulvérisez dans une narine le matin et dans l'autre le soir, ou selon les indications de votre centre. Commencez au 21e jour du cycle pour les protocoles de freinage hypophysaire. | Après l'utilisation - Évitez d'éternuer ou de vous moucher pendant au moins 30 minutes après chaque dose pour assurer une bonne absorption. | ⚠ Dose oubliée - Prenez la dose oubliée dès que vous y pensez, sauf s'il est presque l'heure de la dose suivante - dans ce cas, sautez-la et continuez. Ne doublez jamais la dose. | ⚠ Quand contacter - Si vous avez un rhume ou une congestion nasale, contactez votre centre car l'absorption peut être altérée. Appelez en cas de symptômes inhabituels ou de saignements en dehors du schéma attendu.",
      sideEffects: [
        "Irritation et sécheresse nasales",
        "Bouffées de chaleur",
        "Maux de tête",
        "Sautes d'humeur",
        "Sécheresse vaginale",
        "Baisse de libido",
        "Acné",
        "Douleurs musculaires",
      ],
      storage:
        "Conservez à la verticale à température ambiante (15-30 °C). À protéger de la lumière. Garder le flacon en position verticale pendant le stockage.",
    },
  },
};

registerBlock(leuprolide);
registerBlock(triptorelin);
registerBlock(buserelin);
registerBlock(goserelin);
registerBlock(nafarelin);
